import React, { useRef, useEffect, useState, useContext, SyntheticEvent } from 'react';
import { DataContext } from 'contexts/DataContext';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Business from "@mui/icons-material/Business";
import ModalTitle from 'components/ModalTitle/ModalTitle';

import 'i18n/config';
import { useTranslation } from 'react-i18next';
import { FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import InputAdornment from '@mui/material/InputAdornment';
import { update, getCategories } from './api'
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

interface DetailProps {
    master?: any;
    row: any;
    tasks?: any;
    setDrawerOpen: (open: boolean) => void;
  }
const Detail = (props: DetailProps) => {
    const { t } = useTranslation();
    const [categories, setCategories] = useState([] as any);
    const [saving, setSaving] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);

    const dataContext = useContext(DataContext);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        dataContext.changeShowLoader(true);
        const data = new FormData(event.currentTarget);
        const formDataObject = Object.fromEntries(data.entries());
        formDataObject.start = dayjs(formDataObject.start+"").toISOString();
        formDataObject.end = dayjs(formDataObject.end+"").toISOString();
        update(formDataObject, dataContext.currentAssessment).then((response) => {
            toast.success('Treatment saved', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                });
            
            setSaving(false);
            props.setDrawerOpen(false);
            dataContext.changeShowLoader(false);
            dataContext.toggleReloadParent();
        })
    }
    
    useEffect(() => {
        getCategories().then((response) => {
            setCategories(response)
        })
    },[])

    return (
        <>
        <ModalTitle title={
         <>
           <Business />
           <Typography variant="pageTitle" component="h1" gutterBottom>
           {props.row.name}
           </Typography>
         </>
         
        }
        actions={[
            {
              label: saving ? t('Saving') : t('Save'),
              action: (event: SyntheticEvent) => {
                setSaving(true);
                formRef.current?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
            }
            }
          ]}
       />

        <Box ref={formRef} component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 0 }}>

         <Box
           sx={{
             display: 'flex',
             flexDirection: 'column',
             padding: '30px',

           }}
         >
            <Box sx={{ display: 'flex', gap: '20px', mb: 2 }}>

            <input type="hidden" name="id" defaultValue={props.row.id} />
            <FormControl fullWidth>
            <TextField
                required
                id="name"
                name="name"
                label={t("Name")}
                defaultValue={props.row.name}
                variant="filled"
                sx={{mb:2}}
            />
            </FormControl>
            <FormControl variant="filled" fullWidth>
                <InputLabel id="category-label">{t("Category")}</InputLabel>
                <Select
                labelId="category-label"
                id="category"
                name="category"
                defaultValue={props.row.category}
                >
                {categories.map((category: any, index: number) => {
                    return <MenuItem key={index} value={category}>{category}</MenuItem>
                })}
                </Select>
            </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: '20px', mb: 2 }}>
            <FormControl variant="filled" fullWidth>
                <InputLabel id="depend-label">{t("Depends on")}</InputLabel>
                <Select
                labelId="depend-label"
                id="depend"
                name="depend"
                defaultValue={props.row.depend}
                >
                    <MenuItem value={0}>Nessuno</MenuItem>
                {props.tasks.map((task: any, index: number) => {
                    return <MenuItem key={index} value={task.id}>{task.treatment_id}-{task.name}</MenuItem>
                })}
                </Select>
            </FormControl>
            <FormControl fullWidth>
            <TextField
                required
                id="owner"
                name="owner"
                label={t("Owner")}
                defaultValue={props.row.owner}
                variant="filled"
            />
            </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: '20px', mb: 2 }}>
            <FormControl variant="filled" fullWidth>
                <InputLabel id="implementation-label">{t("Implementation")}</InputLabel>
                <Select
                labelId="implementation-label"
                id="implementation"
                name="implementation"
                defaultValue={props.row.implementation}
                >
                <MenuItem value={"1"}>{t("One shot")}</MenuItem>
                <MenuItem value={"2"}>{t("Recurrent")}</MenuItem>
                </Select>
            </FormControl>
            <FormControl fullWidth>
            <TextField
                required
                id="budget"
                name="budget"
                label={t("Budget")}
                defaultValue={props.row.budget}
                variant="filled"
                type='number'
                InputProps={{
                    endAdornment: <InputAdornment position="end">K €</InputAdornment>,
                }}
            />
            </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: '20px', mb: 2 }}>
            <FormControl fullWidth>
            <TextField
                required
                id="progress"
                name="progress"
                label={t("Progress")}
                defaultValue={props.row.progress}
                variant="filled"
                type='number'
                InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                />
            </FormControl>
            <FormControl variant="filled" fullWidth>
                <InputLabel id="status-label">{t("Status")}</InputLabel>
                <Select
                labelId="status-label"
                id="status"
                name="status"
                defaultValue={props.row.status}
                >
                <MenuItem value={"1"}>{t("Pending")}</MenuItem>
                <MenuItem value={"2"}>{t("Approved")}</MenuItem>
                <MenuItem value={"3"}>{t("Ongoing")}</MenuItem>
                <MenuItem value={"4"}>{t("Completed")}</MenuItem>
                <MenuItem value={"5"}>{t("Canceled")}</MenuItem>
                </Select>
            </FormControl>
            </Box>


            
            <Box sx={{ display: 'flex', gap: '20px', mb: 2 }}>
            <FormControl fullWidth>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker 
                    defaultValue={dayjs(props.row.start)} 
                    slotProps={{ textField: { variant: "filled", label: t("Start"), id: "start", name: "start" } }}
                />
                </LocalizationProvider>
            </FormControl>
            <FormControl fullWidth>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        defaultValue={dayjs(props.row.end)}
                        slotProps={{ textField: { variant: "filled", label: t("End"), id: "end", name: "end" } }}
                    />
                </LocalizationProvider>
            </FormControl>
           </Box>
            <FormControl fullWidth>
            <TextField
                required
                id="description"
                name="description"
                label={t("Description")}
                defaultValue={props.row.description}
                variant="filled"
                multiline
                rows={4}
                sx={{mb:2}}
            />
            </FormControl>
            <FormControl fullWidth>
            <TextField
                required
                id="note"
                name="note"
                label={t("Notes")}
                defaultValue={props.row.note}
                variant="filled"
                multiline
                rows={4}
                sx={{mb:2}}
            />
            </FormControl>
        </Box>

        </Box>
     </>
    )
}

export default Detail